// shared-designer/src/host.tsx · v12.10.25 (#2103 canChangeTemplate - the host decides who sees Change template) · 2026-10-30 · VoiceSpectrum AI
// shared-designer/src/host.tsx · v12.10.3 (BDR-016 step 1 + 2) · 2026-10-03 · VoiceSpectrum AI
//
// THE ONE PLACE THE SHARED WORKFLOW DESIGNER LEARNS WHERE IT IS RUNNING (BDR-016 v1.0 §2). The screen has no login,
// no rights, no URLs and no database of its own: the host app (T2 tenant admin, T4 platform templates) hands it
//   - api      : get/post/put/patch/delete - the host's own authenticated client. T2 passes lib/api as it is; T4 passes an
//                adapter that answers the designer's requests from the platform template catalog (template mode).
//   - toast    : the host's toast.
//   - mode     : 'tenant' (T2 - a tenant's live workflow) | 'template' (T4 - a platform catalog template).
//   - slots    : tenant-only pieces the host owns (T2's Phone & Activation section). A host that has none passes none and
//                the designer says where that setting lives instead.
// The SERVER enforces rights (each tier's controller); the screen only hides what a mode does not have.
import { createElement, forwardRef, type ForwardRefExoticComponent, type RefAttributes } from 'react';

export interface DesignerApi {
  get<T>(path: string, options?: RequestInit): Promise<T>;
  post<T>(path: string, body?: unknown, options?: RequestInit): Promise<T>;
  put<T>(path: string, body?: unknown, options?: RequestInit): Promise<T>;
  patch<T>(path: string, body?: unknown, options?: RequestInit): Promise<T>;
  delete<T>(path: string, options?: RequestInit): Promise<T>;
}
export type DesignerToastType = 'success' | 'error' | 'warning' | 'info' | 'loading';
export interface DesignerToastHandle { id: number; close: () => void; resolve: (type: DesignerToastType, title: string, message?: string) => DesignerToastHandle }
export type DesignerToast = (type: DesignerToastType, title: string, message?: string, opts?: { durationMs?: number }) => DesignerToastHandle;
export type DesignerMode = 'tenant' | 'template';

/** The handle the embedded Phone & Activation section exposes (T2 only). */
export interface PhoneActivationHandle { isDirty(): boolean; save(): Promise<void> }
export type PhoneActivationComponent = ForwardRefExoticComponent<{ embedded?: boolean } & RefAttributes<PhoneActivationHandle>>;

export interface DesignerSlots {
  PhoneActivationSection?: PhoneActivationComponent;
  usePhoneHandlesLabel?: () => string;
}

export interface DesignerHost {
  api: DesignerApi;
  toast?: DesignerToast;
  mode?: DesignerMode;
  slots?: DesignerSlots;
  /** v12.10.25 (#2103): may this person change the practice's template? T2: only on a Support visit. Absent = hidden. */
  canChangeTemplate?: () => boolean;
}

let host: DesignerHost | null = null;

/** Called ONCE by the host app before the designer renders (T2: src/lib/designerHost.ts; T4: src/designer/main.tsx). */
export function configureDesignerHost(h: DesignerHost): void { host = h; }

export function designerHost(): DesignerHost {
  if (!host) throw new Error('shared-designer: configureDesignerHost() was not called by the host app.');
  return host;
}

export function designerMode(): DesignerMode { return host?.mode ?? 'tenant'; }

/** v12.10.25 (#2103): Change template is shown only when the host says so (T2: a Support visit) - never a button that fails. */
export function canChangeTemplate(): boolean { return !!host?.canChangeTemplate?.(); }

/** T2's phone label on the Inbound card; a host without the slot shows a plain note. Stable per app, so hook order holds. */
export function usePhoneHandlesLabel(): string {
  const use = host?.slots?.usePhoneHandlesLabel;
  return use ? use() : 'Phone numbers are set per tenant';
}

/** T2's embedded Phone & Activation section, or - in a host without it - a note saying where it is set. */
export const PhoneActivationSection: PhoneActivationComponent = forwardRef<PhoneActivationHandle, { embedded?: boolean }>(
  function PhoneActivationSlot(props, ref) {
    const C = host?.slots?.PhoneActivationSection;
    if (C) return createElement(C, { ...props, ref });
    return createElement('div', { className: 'wf-host-note' },
      'Phone numbers and activation belong to each tenant - they are set in the tenant’s AI Agent settings (T2), not in a template.');
  });
